import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Github, ArrowLeft } from 'lucide-react';

const SignupPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData.email, formData.username, formData.password);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOAuthSignup = (provider) => {
    // Directly redirect to backend OAuth endpoint
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${API_URL}/api/auth/oauth/${provider.toLowerCase()}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        
        {/* Content */}
        <div className="relative z-10 text-white text-center max-w-lg">
          <img 
            src="https://customer-assets.emergentagent.com/job_create-skills/artifacts/kzrw28ys_Skillerate%20Icon%20White.svg" 
            alt="Skillerate" 
            className="h-32 w-32 mx-auto mb-8"
          />
          <h2 className="text-5xl font-manrope font-bold mb-6 leading-tight">
            Join Skillerate
          </h2>
          <p className="text-xl text-white/90 leading-relaxed">
            Create quality Claude Skills with our visual builder. Build your reputation in the AI ecosystem.
          </p>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white relative">
        {/* Back to Home Button */}
        <Link 
          to="/" 
          className="absolute top-8 right-8 flex items-center gap-2 text-[#6b7280] hover:text-[#e57f3f] transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-manrope font-bold text-[#111827] mb-2">
              Create Account
            </h1>
            <p className="text-[#6b7280]">
              Start building quality Claude Skills today
            </p>
          </div>

          {/* OAuth Buttons */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 hover:bg-gray-50 transition-colors"
              onClick={() => handleOAuthSignup('Google')}
              data-testid="google-signup-button"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 hover:bg-gray-50 transition-colors"
              onClick={() => handleOAuthSignup('GitHub')}
              data-testid="github-signup-button"
            >
              <Github className="w-5 h-5 mr-3" />
              Sign up with GitHub
            </Button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-[#6b7280]">Or sign up with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#111827] font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#111827] font-medium">
                Username
              </Label>
              <Input
                id="username"
                name="username"
                placeholder="johndoe"
                value={formData.username}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="username-input"
              />
              <p className="text-xs text-[#6b7280]">Your public profile URL will be @{formData.username || 'username'}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#111827] font-medium">
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="password-input"
              />
              <p className="text-xs text-[#6b7280]">Must be at least 8 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full h-12 gradient-primary text-white text-base font-semibold hover:opacity-90 transition-opacity"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Terms */}
          <p className="text-xs text-center text-[#6b7280]">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#e57f3f] hover:text-[#c43408]">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#e57f3f] hover:text-[#c43408]">Privacy Policy</Link>
          </p>

          {/* Login Link */}
          <div className="text-center text-sm">
            <span className="text-[#6b7280]">Already have an account? </span>
            <Link 
              to="/login" 
              className="text-[#e57f3f] hover:text-[#c43408] font-semibold transition-colors"
              data-testid="login-link"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;